import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

import axios from "axios";
import { RootState } from "../../app/store";

export interface AdminUser {
  _id: string;
  name: string;
  email: string;
  role: "Admin" | "User";
  verified: boolean;
  outstandingBalance: number;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AdminBill {
  _id: string;
  offer: string;
  user: string;
  type: "new_offer" | "highlighted" | "recommended" | "extend_period";
  title?: string;
  amount: number;
  status: "pending" | "paid" | "cancelled";
  transferTitle?: string;
  createdAt: string;
  paidAt?: string;
}

export interface AdminUserDetails extends AdminUser {
  registerDate?: string;
  updatedAt?: string;
  bills: AdminBill[];
}

interface AdminUserState {
  users: AdminUser[];
  selectedUser: AdminUserDetails | null;
  loading: boolean;
  error: string | null;
}

const initialState: AdminUserState = {
  users: [],
  selectedUser: null,
  loading: false,
  error: null,
};

// ==========================================
// GET ALL USERS
// ==========================================

export const getAdminUsers = createAsyncThunk<
  AdminUser[],
  void,
  { rejectValue: string }
>(
  "adminUsers/getAdminUsers",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        "/api/admin/users",
        {
          headers: {
            ...(token && {
              "x-auth-token": token,
            }),
          },
        }
      );

      return response.data;
    } catch (err) {
      const error = err as any;

      console.error(
        "GET ADMIN USERS ERROR:",
        error.response?.data || error
      );

      return rejectWithValue(
        error.response?.data?.msg ||
          "Błąd podczas pobierania użytkowników"
      );
    }
  }
);

// ==========================================
// GET USER DETAILS
// ==========================================

export const getAdminUserById = createAsyncThunk<
  AdminUserDetails,
  string,
  { rejectValue: string }
>(
  "adminUsers/getAdminUserById",
  async (userId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        `/api/admin/users/${userId}`,
        {
          headers: {
            ...(token && {
              "x-auth-token": token,
            }),
          },
        }
      );

      return response.data;
    } catch (err) {
      const error = err as any;

      console.error(
        "GET ADMIN USER ERROR:",
        error.response?.data || error
      );

      return rejectWithValue(
        error.response?.data?.msg ||
          "Błąd podczas pobierania użytkownika"
      );
    }
  }
);

// ==========================================
// SLICE
// ==========================================

const adminUserSlice = createSlice({
  name: "adminUsers",

  initialState,

  reducers: {
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },

    clearAdminUserError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      // GET ALL USERS
      .addCase(getAdminUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(
        getAdminUsers.fulfilled,
        (state, action: PayloadAction<AdminUser[]>) => {
          state.loading = false;
          state.users = action.payload;
        }
      )

      .addCase(getAdminUsers.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload ||
          "Nie udało się pobrać użytkowników";
      })

      // GET USER DETAILS
      .addCase(getAdminUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedUser = null;
      })

      .addCase(
        getAdminUserById.fulfilled,
        (state, action: PayloadAction<AdminUserDetails>) => {
          state.loading = false;
          state.selectedUser = action.payload;
        }
      )

      .addCase(getAdminUserById.rejected, (state, action) => {
        state.loading = false;
        state.selectedUser = null;
        state.error =
          action.payload ||
          "Nie udało się pobrać użytkownika";
      });
  },
});

// ==========================================
// ACTIONS
// ==========================================

export const {
  clearSelectedUser,
  clearAdminUserError,
} = adminUserSlice.actions;

// ==========================================
// SELECTORS
// ==========================================

export const selectAdminUsers = (state: RootState) =>
  state.adminUser.users;

export const selectAdminSelectedUser = (state: RootState) =>
  state.adminUser.selectedUser;

export const selectAdminUsersLoading = (state: RootState) =>
  state.adminUser.loading;

export const selectAdminUsersError = (state: RootState) =>
  state.adminUser.error;

export default adminUserSlice.reducer;